import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ContentChild,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { PopoverModule } from 'primeng/popover';
import { MessageService } from 'primeng/api';
import {
  BesharpChatService,
  BesharpChatHistoryMessage,
  BesharpChatQuota,
  BesharpChatReference,
} from './services/besharp-chat.service';
import { OverflowsDirective } from './directives/overflows.directive';
import { MarkdownPipe } from './pipes/markdown.pipe';
import { MentionsPipe } from './pipes/mentions.pipe';
import { chartMentionLabel, escapeHtml } from './pipes/mention-chip';
import { MermaidService } from './services/mermaid.service';
import { DataTableService } from './services/datatable.service';
import { PlotlyService } from './services/plotly.service';
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';

/** Usage share at which the meter turns amber and a one-time toast warns the user. */
const QUOTA_WARN_PERCENT = 80;
/** Usage share at which the meter turns red. */
const QUOTA_CRITICAL_PERCENT = 95;

/** Rows shown in the `@` menu at once. */
const MENTION_ROWS = 8;

/** Which kinds the `@` menu is currently offering. */
export type MentionFilter = 'all' | 'indicator' | 'country' | 'region';

/**
 * One node of a tool payload rendered as a tree.
 *
 * `path` is a stable identity for the expand/collapse map, so a node keeps its state when
 * a streaming payload is re-parsed. `depth` drives both the indent rail and whether the
 * node starts open.
 */
export type BesharpJsonNode = {
  /** Property name, array index, or null at the root. */
  key: string | null;
  kind: 'object' | 'array' | 'string' | 'number' | 'boolean' | 'null';
  /** Rendered scalar text; absent on containers. */
  value?: string;
  children?: BesharpJsonNode[];
  /** Entry count, on containers only. */
  size?: number;
  depth: number;
  path: string;
};

/** Cache sentinel: distinguishes "parsed to nothing" from "not cached yet". */
const NO_JSON = Symbol('no-json');

const MENTION_FILTERS: { value: MentionFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'indicator', label: 'Indicators' },
  { value: 'country', label: 'Countries' },
  { value: 'region', label: 'Regions' },
];

// ─── Public types ─────────────────────────────────────────────────────────────

export type BesharpChatMessageRole = 'user' | 'assistant';

export type BesharpChatToolStep = {
  /** Whether this step is a tool invocation or its result. */
  type: 'tool_call' | 'tool_result' | 'thinking' | 'agent_thinking' | 'sub_agent_thinking';
  content: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
};

/**
 * One row of the reasoning panel.
 *
 * `call_group` carries its own `children`: tools a sub-agent called on its own behalf, which
 * the agent tags with `metadata.parent`. Orchestrator calls have no parent and stay at the
 * top level, which is what separates "the agent called this tool" from "the agent handed the
 * job to a sub-agent, and that sub-agent called these tools".
 */
export type BesharpChatStepGroup =
  | { kind: 'standalone'; step: BesharpChatToolStep; index: number }
  | {
      kind: 'call_group';
      call: BesharpChatToolStep;
      results: BesharpChatToolStep[];
      callIndex: number;
      children: BesharpChatStepGroup[];
    };

export type BesharpChatEmptyState = {
  icon?: string;
  title?: string;
  description?: string;
  suggestions?: string[];
};

export type BesharpChatMessage = {
  id: string;
  role: BesharpChatMessageRole;
  content: string;
  timestamp: string;
  trace_id?: string;
  /** True while the assistant is still streaming this message. */
  isStreaming?: boolean;
  /** Intermediate tool-call / tool-result steps produced during streaming. */
  toolSteps?: BesharpChatToolStep[];
  /** True for messages loaded from server-side conversation history. */
  isFromHistory?: boolean;
};

/**
 * Configuration object passed via [config] input.
 * All fields are optional.
 */
export type BesharpChatConfig = {
  /** Session ID used to load history and route invocations. */
  sessionId?: string;
  /** Textarea placeholder text. Default: "Ask something…" */
  placeholder?: string;
  /** Display name for the assistant. Default: "Assistant" */
  botName?: string;
  /** Display name for the current user. Default: "You" */
  userLabel?: string;
  /** Model name forwarded to the invocation endpoint. */
  model?: string;
  /** Max agent turns forwarded to the invocation endpoint. */
  maxTurns?: number;
  /** Whether to show HH:MM timestamps on messages. Default: true */
  showTimestamps?: boolean;
  /** Empty-state message shown in the center when there are no messages. */
  emptyState?: BesharpChatEmptyState;
};

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-besharp-chat',
  imports: [
    CommonModule,
    FormsModule,
    SkeletonModule,
    ButtonModule,
    TagModule,
    PopoverModule,
    OverflowsDirective,
    MarkdownPipe,
    MentionsPipe,
    DatePipe,
  ],
  templateUrl: './besharp-chat.component.html',
  styleUrl: './besharp-chat.component.scss',
})
export class BesharpChatComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);
  private mermaid = inject(MermaidService);
  private plotly = inject(PlotlyService);
  private dataTable = inject(DataTableService);
  /** Provided by the in-app layout; absent when the chat is embedded elsewhere. */
  private messageService = inject(MessageService, { optional: true });

  /** The chat backend service. Must extend BesharpChatService. */
  @Input({ required: true }) service!: BesharpChatService;

  /** Configuration for the chat session, labels, and model options. */
  @Input() config: BesharpChatConfig = {};

  /** Emitted the first time a session ID is assigned (i.e. a new conversation starts). */
  @Output() sessionCreated = new EventEmitter<string>();

  /** Optional projected template for the empty-state block. */
  @ContentChild('emptyStateTemplate')
  protected emptyStateTemplate?: TemplateRef<{ $implicit: BesharpChatEmptyState }>;

  @ViewChild('messagesContainer') private messagesContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('textInput') private textInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('mentionMenu') private mentionMenu?: ElementRef<HTMLElement>;

  protected messages: BesharpChatMessage[] = [];
  protected inputText = '';
  protected isLoadingHistory = false;
  protected isStreaming = false;
  protected errorMessage: string | null = null;

  protected modelOptions = [
    { label: 'Claude Sonnet 5.5', value: 'eu.anthropic.claude-sonnet-5-5' },
    { label: 'Claude Sonnet 4.6', value: 'eu.anthropic.claude-sonnet-4-6' },
    { label: 'Claude Haiku 4.5', value: 'eu.anthropic.claude-haiku-4-5-20251001-v1:0' },
    { label: 'Amazon Nova 2 Lite', value: 'eu.amazon.nova-2-lite-v1:0' },
  ];
  protected selectedModel = this.modelOptions[0].value;

  /** Tracks expanded state for each message's tool-steps panel. */
  protected toolStepsExpanded = new Map<string, boolean>();
  protected expandedSteps = new Map<string, boolean>();

  /** Used to cancel the streaming loop on component destroy. */
  private destroyed = false;
  /** Prevents concurrent mermaid renders triggered by ngAfterViewChecked. */
  private renderingMermaid = false;
  /** Prevents concurrent plotly renders triggered by ngAfterViewChecked. */
  private renderingPlotly = false;
  /** Same, for data tables. */
  private renderingDataTable = false;
  /** Streaming tokens queued and replayed word-by-word for smoother output. */
  private pendingWordQueue: string[] = [];
  private streamWordIntervalId?: number;
  private readonly streamWordIntervalMs = 26;
  private scrollFrame: number | null = null;
  /** Re-renders the "available again in …" countdown once a minute while blocked. */
  private quotaClock?: ReturnType<typeof setInterval>;

  constructor() {
    effect(() => {
      const quota = this.quota;
      this.warnOnQuotaThreshold(quota);
      this.syncQuotaClock(quota);
    });
  }

  // ─── Usage quota ────────────────────────────────────────────────────────────

  protected get quota(): BesharpChatQuota | null {
    return this.service?.quota?.() ?? null;
  }

  /** The composer is locked until the daily reset; history and exports stay usable. */
  protected get quotaBlocked(): boolean {
    return this.quota?.exhausted ?? false;
  }

  protected get quotaLevel(): 'ok' | 'warn' | 'critical' {
    const percent = this.quota?.percent ?? 0;
    if (percent >= QUOTA_CRITICAL_PERCENT) return 'critical';
    if (percent >= QUOTA_WARN_PERCENT) return 'warn';
    return 'ok';
  }

  /** "3h 12m" until the reset, for the blocked banner. */
  protected get quotaResetIn(): string {
    const quota = this.quota;
    if (!quota) return '';
    const minutes = Math.max(0, Math.ceil((new Date(quota.resetsAt).getTime() - Date.now()) / 60_000));
    const hours = Math.floor(minutes / 60);
    return hours ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
  }

  /**
   * Whole percent for display, truncated rather than rounded: 99.7% must read "99%", not a
   * "100%" that is not a block yet.
   */
  protected wholePercent(percent: number | null): number {
    return Math.floor(percent ?? 0);
  }

  /** Ring geometry: r = 8 in a 20×20 viewBox. */
  protected readonly ringCircumference = 2 * Math.PI * 8;

  protected ringOffset(percent: number | null): number {
    return this.ringCircumference * (1 - Math.min(100, percent ?? 0) / 100);
  }

  /** The popover always shows the full, fresh report — the live updates only patch today. */
  protected onQuotaPopoverShow(): void {
    void this.service.refreshQuota?.();
  }

  private warnOnQuotaThreshold(quota: BesharpChatQuota | null): void {
    if (!quota || quota.unlimited || quota.exhausted || (quota.percent ?? 0) < QUOTA_WARN_PERCENT) return;
    // Once per UTC day (the day before resetsAt), per browser — a convenience, not state.
    const key = `besharp-chat.quota-warned.${quota.resetsAt.slice(0, 10)}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      // Storage unavailable: warn anyway, at worst more than once.
    }
    this.messageService?.add({
      severity: 'warn',
      summary: 'Usage',
      detail: `You have used ${this.wholePercent(quota.percent)}% of today's quota.`,
      life: 8000,
    });
  }

  private syncQuotaClock(quota: BesharpChatQuota | null): void {
    clearInterval(this.quotaClock);
    this.quotaClock = undefined;
    if (quota?.exhausted) {
      this.quotaClock = setInterval(() => this.cdr.markForCheck(), 60_000);
    }
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  async ngOnInit(): Promise<void> {
    // The catalogue is not needed to type, so it must never delay the first render.
    void this.loadReferences();
    void this.service.refreshQuota?.();
    await this.loadHistory();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!changes['config'] || changes['config'].firstChange) return;

    const prev = changes['config'].previousValue as BesharpChatConfig;
    const curr = changes['config'].currentValue as BesharpChatConfig;

    if (prev?.sessionId !== curr?.sessionId) {
      this.messages = [];
      this.toolStepsExpanded.clear();
      this.expandedSteps.clear();
      await this.loadHistory();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearInterval(this.quotaClock);
    this.stopWordPlayback();
    if (this.scrollFrame !== null) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
  }

  // Fires after every Angular DOM update. We use this to reliably trigger
  // mermaid rendering once the [innerHTML] binding has settled, regardless
  // of whether the content came from streaming or history loading.
  ngAfterViewChecked(): void {
    const container = this.messagesContainer?.nativeElement;
    if (!container || this.isStreaming) return;

    // Query-result tables are excluded on purpose. They reuse .markdown-table for its look,
    // but this button builds its workbook by scraping the rendered DOM, and that table is
    // capped at the first 2000 rows — the user would get a silent slice of a large result,
    // indistinguishable from the whole thing. DataTableService gives them their own button
    // that downloads the complete export from the API instead.
    const tables = container.querySelectorAll('.markdown-table:not(.has-download-btn):not(.datatable-table)');

    tables.forEach((el: Element) => {
      const table = el as HTMLTableElement;
      table.classList.add('has-download-btn');

      const wrapper = document.createElement('div');
      wrapper.className = 'table-wrapper';
      wrapper.style.position = 'relative';
      wrapper.style.display = 'inline-block';
      wrapper.style.maxWidth = '100%';
      wrapper.style.overflowX = 'auto';

      table.parentNode?.insertBefore(wrapper, table);
      wrapper.appendChild(table);

      const btn = document.createElement('button');
      btn.className = 'download-btn pi pi-download';
      btn.title = 'Download Excel Table';

      btn.addEventListener('click', () => {
        this.downloadTable(table);
      });

      wrapper.appendChild(btn);
    });

    if (!this.renderingMermaid && container.querySelector('.mermaid-diagram[data-mermaid-src]')) {
      this.renderingMermaid = true;
      this.mermaid.renderDiagrams(container).finally(() => {
        this.renderingMermaid = false;
      });
    }

    if (!this.renderingPlotly && container.querySelector('.plotly-diagram[data-plotly-src]')) {
      this.renderingPlotly = true;
      this.plotly.renderDiagrams(container).finally(() => {
        this.renderingPlotly = false;
      });
    }

    // Runs after PlotlyService has drawn a chart and stamped its id on the element, which
    // is a later pass than the one that started the render — hence a separate query rather
    // than a callback: this hook fires on every change-detection cycle anyway.
    const charts = container.querySelectorAll('.plotly-diagram[data-chart-id]:not(.has-pin-btn)');

    charts.forEach((el: Element) => {
      const chart = el as HTMLElement;
      chart.classList.add('has-pin-btn');

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'chart-pin-btn pi pi-at';
      btn.title = 'Ask about this chart';

      // A sibling, not a child: the element it sits next to is Plotly's own container, and
      // anything left inside it is fair game for the next purge.
      btn.addEventListener('click', () => this.pinChart(chart.dataset['chartId'] ?? '', chart.dataset['chartTitle']));

      chart.parentNode?.insertBefore(btn, chart.nextSibling);
    });

    if (!this.renderingDataTable && container.querySelector('.data-table[data-datatable-src]')) {
      this.renderingDataTable = true;
      this.dataTable.renderTables(container).finally(() => {
        this.renderingDataTable = false;
      });
    }
  }

  // ─── History loading ─────────────────────────────────────────────────────────

  private async loadHistory(): Promise<void> {
    if (!this.config?.sessionId) return;

    this.isLoadingHistory = true;
    this.errorMessage = null;

    try {
      const history = await this.service.getConversationHistory(this.config.sessionId);
      this.messages = history.map((m) => this.historyToMessage(m));
      this.scrollToBottom();
    } catch {
      this.errorMessage = 'Failed to load conversation history.';
    } finally {
      this.isLoadingHistory = false;
    }
  }

  private historyToMessage(msg: BesharpChatHistoryMessage): BesharpChatMessage {
    return {
      id: uuidv4(),
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp,
      toolSteps: msg.reasoningSteps?.map((step) => ({
        type: step.type,
        content: step.content,
        timestamp: step.timestamp,
        metadata: step.metadata,
      })),
      isFromHistory: true,
    };
  }

  // ─── Sending messages ────────────────────────────────────────────────────────

  protected async sendMessage(): Promise<void> {
    const raw = this.inputText.trim();
    if (!raw || this.isStreaming || this.quotaBlocked) return;

    const text = this.expandMentions(raw);

    this.errorMessage = null;
    this.inputText = '';
    this.pendingMentions = [];
    this.closeMentionMenu();
    this.syncComposerHighlight();
    this.resetTextareaHeight();

    // Add user bubble immediately.
    const userMessage: BesharpChatMessage = {
      id: uuidv4(),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    this.messages.push(userMessage);

    // Add empty assistant bubble that will be filled by the stream.
    const botMessage: BesharpChatMessage = {
      id: uuidv4(),
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true,
      toolSteps: [],
    };
    this.messages.push(botMessage);
    this.scrollToBottom();

    this.isStreaming = true;

    const isNewSession = !this.config.sessionId;
    let sentSessionId = false;
    this.config.sessionId = this.config.sessionId ?? uuidv4();

    try {
      const stream = this.service.sendMessageStream({
        message: text,
        sessionId: this.config.sessionId,
        model: this.selectedModel ?? this.config.model,
        max_turns: this.config.maxTurns,
      });

      for await (const event of stream) {
        if (event.type === 'quota_exceeded' || event.type === 'quota_unavailable') {
          // Refused before the agent ran: nothing was sent, so undo the optimistic bubbles
          // and give the prompt back. A refused first message must not create a session.
          this.messages = this.messages.filter((m) => m !== userMessage && m !== botMessage);
          this.inputText = raw;
          this.syncComposerHighlight();
          if (isNewSession) this.config.sessionId = undefined;
          // quota_exceeded is rendered by the blocked banner; only the transient case is an error.
          if (event.type === 'quota_unavailable') this.errorMessage = event.content;
          break;
        }
        if (isNewSession && !sentSessionId) {
          this.sessionCreated.emit(this.config.sessionId!);
          sentSessionId = true;
        }
        if (this.destroyed) break;

        if (event.type === 'text_chunk') {
          this.queueStreamChunk(botMessage, event.content);
        } else if (event.type === 'agent_thinking') {
          this.flushPendingStreamChunk(botMessage);
          botMessage.toolSteps!.push({
            type: 'agent_thinking',
            content: event.content,
            timestamp: event.timestamp,
            metadata: event.metadata,
          });
          this.cdr.detectChanges();
        } else if (event.type === 'content_reset') {
          this.flushPendingStreamChunk(botMessage);
          botMessage.content = '';
          this.cdr.detectChanges();
        } else if (event.type === 'sub_agent_thinking') {
          // Appends to the live step rather than replacing it, so the panel shows the
          // sub-agent's text growing. A new step starts only when a different sub-agent
          // takes over.
          const steps = botMessage.toolSteps!;
          const last = steps[steps.length - 1];
          const subAgent = event.metadata?.['sub_agent'];
          if (last?.type === 'sub_agent_thinking' && last.metadata?.['sub_agent'] === subAgent) {
            last.content += event.content;
          } else {
            this.flushPendingStreamChunk(botMessage);
            steps.push({
              type: 'sub_agent_thinking',
              content: event.content,
              timestamp: event.timestamp,
              metadata: event.metadata,
            });
          }
          this.cdr.detectChanges();
        } else if (event.type === 'tool_call' || event.type === 'tool_result') {
          this.flushPendingStreamChunk(botMessage);
          botMessage.toolSteps!.push({
            type: event.type,
            content: event.content,
            timestamp: event.timestamp,
            metadata: event.metadata,
          });
          this.cdr.detectChanges();
        } else if (event.type === 'trace_id') {
          // Arrives once the answer is fully generated, right before 'end' — this is what
          // gates the feedback buttons on, so they only ever appear on a settled message.
          botMessage.trace_id = event.content;
          this.cdr.detectChanges();
        } else if (event.type === 'error') {
          this.flushPendingStreamChunk(botMessage);
          this.errorMessage = event.content;
          break;
        } else if (event.type === 'end') {
          this.flushPendingStreamChunk(botMessage);
          break;
        }
      }
    } catch (err) {
      this.flushPendingStreamChunk(botMessage);
      this.errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred.';
    } finally {
      this.flushPendingStreamChunk(botMessage);
      botMessage.isStreaming = false;
      this.isStreaming = false;
      sentSessionId = false;
      this.cdr.detectChanges();
      this.scrollToBottom();
    }
  }

  // ─── @ mentions ─────────────────────────────────────────────────────────────

  /** Everything the user can pin with `@`. Stays empty when the backend has no catalogue. */
  protected references: BesharpChatReference[] = [];

  /**
   * References the user actually picked, in insertion order.
   *
   * Not rendered as a separate list: the text in the composer is drawn as a chip instead,
   * so the pin is visible where the words are.
   */
  protected pendingMentions: { label: string; code: string; kind: string }[] = [];

  protected mentionMatches: BesharpChatReference[] = [];
  protected mentionActive = 0;
  protected mentionOpen = false;
  protected mentionQuery = '';

  protected readonly mentionFilters = MENTION_FILTERS;
  protected mentionFilter: MentionFilter = 'all';
  /** Per-kind totals for the tab badges, counted before the filter and the row cap. */
  protected mentionCounts: Record<MentionFilter, number> = { all: 0, indicator: 0, country: 0, region: 0 };

  /** Everything the query matched, kept so the tabs can show counts for the other kinds. */
  private mentionAllMatches: BesharpChatReference[] = [];

  /**
   * Chip outlines drawn behind the textarea's own text.
   *
   * A `<textarea>` renders one flat string — it cannot hold styled inline elements. The
   * usual way round that is a mirror: a div with identical typography and padding sits
   * directly behind, showing the same text with the confirmed references boxed. The
   * textarea keeps the caret, the selection and IME; the mirror only draws.
   */
  protected composerHighlight: SafeHtml = '';

  private async loadReferences(): Promise<void> {
    if (!this.service.getReferences) return;
    this.references = await this.service.getReferences();
  }

  /**
   * The `@…` fragment immediately before the caret, if there is one.
   *
   * Labels contain spaces, so the query may too; the length cap stops a stray `@`
   * typed mid-sentence from hijacking the composer indefinitely.
   */
  private mentionContext(): { start: number; query: string } | null {
    const element = this.textInput?.nativeElement;
    if (!element || !this.references.length) return null;

    const caret = element.selectionStart ?? 0;
    const match = /(?:^|\s)@([^\n@]{0,48})$/.exec(this.inputText.slice(0, caret));
    if (!match) return null;

    const start = caret - match[1].length - 1;

    // A mention that has already been accepted still looks like an open `@…` fragment to
    // the regex above, because labels contain spaces. Without this the menu would spring
    // back open the moment the user picked something and carried on typing.
    //
    // The caret must be strictly past the token, not merely at its end: accepting appends
    // a trailing space, so a settled mention always leaves the caret beyond it, while a
    // user typing a second reference that happens to share a label — `Fixed-telephone
    // subscriptions` exists four times — still lands exactly on the end and must open.
    const fragment = this.inputText.slice(start);
    for (const mention of this.pendingMentions) {
      const token = `@${mention.label}`;
      if (fragment.startsWith(token) && caret > start + token.length) return null;
    }

    return { start, query: match[1] };
  }

  /** Everything the query matches, best first. Filtering and the row cap come later. */
  private rankReferences(query: string): BesharpChatReference[] {
    const needle = query.trim().toLowerCase();
    if (!needle) return [...this.references];

    const tokens = needle.split(/\s+/);
    const scored: { score: number; reference: BesharpChatReference }[] = [];

    for (const reference of this.references) {
      const label = reference.label.toLowerCase();
      const haystack =
        `${label} ${reference.path} ${reference.unit} ${reference.code} ${reference.capital}`.toLowerCase();
      if (!tokens.every((token) => haystack.includes(token))) continue;

      const score = label.startsWith(needle) ? 3 : tokens.every((token) => label.includes(token)) ? 2 : 1;
      scored.push({ score, reference });
    }
    scored.sort((a, b) => b.score - a.score);

    return scored.map((entry) => entry.reference);
  }

  /** The rows actually shown: the active tab's kind, capped to what the menu can hold. */
  private visibleMentions(matches: BesharpChatReference[]): BesharpChatReference[] {
    if (this.mentionFilter !== 'all') {
      return matches.filter((match) => match.kind === this.mentionFilter).slice(0, MENTION_ROWS);
    }

    const kinds: BesharpChatReference['kind'][] = ['indicator', 'country', 'region'];
    const buckets = kinds.map((kind) => ({ kind, matches: matches.filter((match) => match.kind === kind) }));
    let remainingRows = MENTION_ROWS;
    let remainingKinds = buckets.length;
    const allocations = new Map<BesharpChatReference['kind'], number>();
    for (const bucket of [...buckets].sort((a, b) => a.matches.length - b.matches.length)) {
      const slots = Math.min(bucket.matches.length, Math.ceil(remainingRows / remainingKinds));
      allocations.set(bucket.kind, slots);
      remainingRows -= slots;
      remainingKinds--;
    }
    return buckets.flatMap((bucket) => bucket.matches.slice(0, allocations.get(bucket.kind) ?? 0));
  }

  protected setMentionFilter(filter: MentionFilter): void {
    this.mentionFilter = filter;
    this.mentionMatches = this.visibleMentions(this.mentionAllMatches);
    this.mentionActive = 0;
    if (this.mentionMenu) this.mentionMenu.nativeElement.scrollTop = 0;
  }

  protected mentionFilterLabel(): string {
    return MENTION_FILTERS.find((filter) => filter.value === this.mentionFilter)?.label ?? 'References';
  }

  /** Left/right step through the tabs, wrapping at both ends. */
  protected cycleMentionFilter(delta: number): void {
    const index = MENTION_FILTERS.findIndex((filter) => filter.value === this.mentionFilter);
    const next = (index + delta + MENTION_FILTERS.length) % MENTION_FILTERS.length;
    this.setMentionFilter(MENTION_FILTERS[next].value);
  }

  /**
   * Drops pinned references whose text the user has deleted.
   *
   * The chips under the composer promise that those codes will be sent. If the text is
   * gone the promise is false — and worse, a stale entry would re-attach its code the
   * moment the same words reappeared somewhere else in the message.
   */
  private prunePendingMentions(): void {
    // Occurrences are consumed as they are matched, so two pins sharing a label each need
    // their own occurrence to survive. Blanking with spaces keeps the offsets, and the
    // word boundaries, intact for the pins still to be checked.
    let remaining = this.inputText;
    const kept: typeof this.pendingMentions = [];

    for (const mention of this.pendingMentions) {
      const token = `@${mention.label}`;
      const at = this.findMention(remaining, token);
      if (at === -1) continue;

      kept.push(mention);
      remaining = remaining.slice(0, at) + ' '.repeat(token.length) + remaining.slice(at + token.length);
    }

    this.pendingMentions = kept;
  }

  /**
   * Rebuilds the mirror. Called wherever the text or the pins change — not from the
   * template, which would recompute it on every change-detection pass of a streaming chat.
   */
  private syncComposerHighlight(): void {
    // Consume occurrences as they match, so two pins sharing a label get one box each.
    let scan = this.inputText;
    const boxes: { start: number; end: number; kind: string }[] = [];

    for (const mention of this.pendingMentions) {
      const token = `@${mention.label}`;
      const at = this.findMention(scan, token);
      if (at === -1) continue;
      boxes.push({ start: at, end: at + token.length, kind: mention.kind });
      scan = scan.slice(0, at) + ' '.repeat(token.length) + scan.slice(at + token.length);
    }
    boxes.sort((a, b) => a.start - b.start);

    let html = '';
    let cursor = 0;
    for (const box of boxes) {
      const variant =
        box.kind === 'country'
          ? ' is-country'
          : box.kind === 'region'
            ? ' is-region'
            : box.kind === 'chart'
              ? ' is-chart'
              : '';
      html += escapeHtml(this.inputText.slice(cursor, box.start));
      html += `<span class="composer-chip${variant}">${escapeHtml(this.inputText.slice(box.start, box.end))}</span>`;
      cursor = box.end;
    }
    html += escapeHtml(this.inputText.slice(cursor));

    // A trailing newline collapses in a div but not in a textarea; the zero-width space
    // keeps the mirror's height in step with what is being typed.
    this.composerHighlight = this.sanitizer.bypassSecurityTrustHtml(`${html}\u200b`);
  }

  protected refreshMentionMenu(): void {
    this.prunePendingMentions();
    this.syncComposerHighlight();

    const context = this.mentionContext();
    if (!context) {
      this.closeMentionMenu();
      return;
    }
    this.mentionAllMatches = this.rankReferences(context.query);
    this.mentionCounts = {
      all: this.mentionAllMatches.length,
      indicator: this.mentionAllMatches.filter((match) => match.kind === 'indicator').length,
      country: this.mentionAllMatches.filter((match) => match.kind === 'country').length,
      region: this.mentionAllMatches.filter((match) => match.kind === 'region').length,
    };
    this.mentionMatches = this.visibleMentions(this.mentionAllMatches);
    this.mentionActive = 0;
    this.mentionOpen = true;
    this.mentionQuery = context.query.trim();

    // A new query means a new list: start it at the top rather than wherever the last
    // one was left scrolled to.
    setTimeout(() => {
      if (this.mentionMenu) this.mentionMenu.nativeElement.scrollTop = 0;
    });
  }

  /**
   * The grey line under a menu row. Countries carry no unit or years, indicators no
   * capital, so the parts are assembled from whatever the entry actually has.
   */
  protected mentionMetaFor(reference: BesharpChatReference): string {
    return [
      reference.path,
      reference.unit,
      reference.years,
      reference.disaggregations.join(', '),
      reference.capital && `capital ${reference.capital}`,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  protected closeMentionMenu(): void {
    this.mentionOpen = false;
    this.mentionMatches = [];
    this.mentionAllMatches = [];
    // Back to "All" for the next mention: a filter left over from three messages ago is
    // the kind of hidden state that makes a menu look broken.
    this.mentionFilter = 'all';
  }

  protected moveMentionSelection(delta: number): void {
    const count = this.mentionMatches.length;
    if (!count) return;
    this.mentionActive = (this.mentionActive + delta + count) % count;
    this.scrollActiveMentionIntoView();
  }

  /** Pointer and keyboard drive the same selection, so only ever one row looks chosen. */
  protected setMentionActive(index: number): void {
    this.mentionActive = index;
  }

  /**
   * Drags the list along with the keyboard selection.
   *
   * The menu holds more rows than fit its max-height, so without this, arrowing past the
   * bottom moves a highlight the user can no longer see.
   */
  private scrollActiveMentionIntoView(): void {
    // The row only carries `is-active` once change detection has run.
    setTimeout(() => {
      const options = this.mentionMenu?.nativeElement.querySelectorAll<HTMLElement>('.mention-option');
      options?.[this.mentionActive]?.scrollIntoView?.({ block: 'nearest' });
    });
  }

  protected acceptMention(reference: BesharpChatReference): void {
    const context = this.mentionContext();
    const element = this.textInput?.nativeElement;
    if (!context || !element) return;

    const before = this.inputText.slice(0, context.start);
    const after = this.inputText.slice(element.selectionStart ?? 0);
    const token = `@${reference.label} `;

    this.inputText = before + token + after;
    this.pendingMentions.push({ label: reference.label, code: reference.code, kind: reference.kind });
    this.closeMentionMenu();
    this.syncComposerHighlight();

    // The value goes through ngModel, so the caret can only be restored once Angular
    // has written it back into the textarea.
    const caret = before.length + token.length;
    setTimeout(() => {
      element.focus();
      element.setSelectionRange(caret, caret);
    });
  }

  /**
   * Pins a chart the assistant drew, from the button sitting under it.
   *
   * The `@` menu cannot offer charts: a chart_id is not something a user could search for
   * or type, and the only chart they mean is the one they are looking at. So the pin comes
   * from the chart itself and lands in the composer as an ordinary mention — from there it
   * travels, renders and prunes exactly like an indicator, and reaches the agent as
   * `@[title](chart:id)`, which is what tells it WHICH chart a follow-up is about.
   *
   * Without it, "make it red" in a conversation holding three charts is a question the
   * agent has to ask back.
   */
  protected pinChart(chartId: string, chartTitle?: string): void {
    if (!chartId) return;

    const element = this.textInput?.nativeElement;
    const label = chartMentionLabel(chartTitle);
    const token = `@${label} `;

    // At the caret, so a pin can be dropped mid-sentence; at the end when the composer has
    // never been focused and there is no caret to speak of.
    const caret = element?.selectionStart ?? this.inputText.length;
    const before = this.inputText.slice(0, caret);
    const after = this.inputText.slice(caret);
    // findMention only accepts a token that starts at a whitespace boundary, so a pin
    // dropped straight after a word would be sent as prose with its id silently lost.
    const spacer = before && !/\s$/.test(before) ? ' ' : '';

    this.inputText = before + spacer + token + after;
    this.pendingMentions.push({ label, code: chartId, kind: 'chart' });
    this.closeMentionMenu();
    this.syncComposerHighlight();

    const at = before.length + spacer.length + token.length;
    setTimeout(() => {
      element?.focus();
      element?.setSelectionRange(at, at);
    });
  }

  /**
   * Attaches its code to every mention still present in the text.
   *
   * A mention whose label the user edited afterwards is simply not found, and goes out as
   * plain prose — the orchestrator then disambiguates it the way it does any typed
   * request. Losing a code costs a clarifying question; attaching the wrong one would cost
   * a wrong answer, so the lookup is deliberately strict.
   */
  private expandMentions(text: string): string {
    let expanded = text;

    for (const mention of this.pendingMentions) {
      const token = `@${mention.label}`;
      const index = this.findMention(expanded, token);
      if (index === -1) continue;

      expanded =
        expanded.slice(0, index) +
        `@[${mention.label}](${mention.kind}:${mention.code})` +
        expanded.slice(index + token.length);
    }

    return expanded;
  }

  /**
   * Index of `token` where it stands as a whole mention, or -1.
   *
   * The bounds are what make editing safe. A plain `indexOf` matches the prefix of a
   * longer word, so a user who picked `@Oman` and then typed on to make it
   * `@Omani data` would get Oman's code pinned to a phrase they did not choose — worse
   * than losing the code, which is the intended degradation.
   */
  private findMention(text: string, token: string): number {
    for (let from = 0; from <= text.length; ) {
      const index = text.indexOf(token, from);
      if (index === -1) return -1;

      const before = index === 0 ? '' : text.charAt(index - 1);
      const after = text.charAt(index + token.length);
      if (!/\S/.test(before) && !/[\p{L}\p{N}]/u.test(after || '')) return index;

      from = index + 1;
    }
    return -1;
  }

  // ─── Template helpers ────────────────────────────────────────────────────────

  protected onKeydown(event: KeyboardEvent): void {
    // The mention menu owns Enter and Tab while it is open. Without this, picking an
    // option would submit the message instead of inserting the reference.
    if (this.mentionOpen && this.mentionMatches.length) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveMentionSelection(1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveMentionSelection(-1);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        this.acceptMention(this.mentionMatches[this.mentionActive]);
        return;
      }
    }

    // Left/right switch tabs, but only bare: with a modifier they stay what they are in
    // any text field — select by character, jump by word, go to the line's end.
    const bareArrow = !event.shiftKey && !event.altKey && !event.metaKey && !event.ctrlKey;
    if (this.mentionOpen && bareArrow && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      this.cycleMentionFilter(event.key === 'ArrowRight' ? 1 : -1);
      return;
    }

    if (this.mentionOpen && event.key === 'Escape') {
      event.preventDefault();
      this.closeMentionMenu();
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /** Keeps the mirror aligned when the textarea scrolls past its max height. */
  protected syncMirrorScroll(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    const mirror = target.previousElementSibling as HTMLElement | null;
    if (mirror) mirror.scrollTop = target.scrollTop;
  }

  protected onInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;

    // ngModel registers its own `input` listener as a directive host binding, which runs
    // after this template binding — so `inputText` is still one keystroke behind here.
    // Take the value the user actually typed, or the menu opens a character late.
    this.inputText = target.value;

    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
    this.refreshMentionMenu();
  }

  protected toggleToolSteps(messageId: string): void {
    this.toolStepsExpanded.set(messageId, !this.toolStepsExpanded.get(messageId));
  }

  protected isToolStepsExpanded(messageId: string): boolean {
    return this.toolStepsExpanded.get(messageId) ?? false;
  }

  /**
   * Whether a result is taller than its clamp, reported by the `besharpOverflows`
   * directive rather than guessed from the payload's character count — see that file for
   * why the count was the wrong question.
   */
  protected setResultOverflow(key: string, overflows: boolean): void {
    if (this.resultOverflow.get(key) === overflows) return;
    this.resultOverflow.set(key, overflows);
    this.cdr.markForCheck();
  }

  /**
   * Show the expand control when there is more to see, and keep it while expanded so the
   * result can be collapsed again — once open, a result that now fits its box reports no
   * overflow, and the control would otherwise vanish mid-interaction.
   */
  protected showsResultToggle(key: string): boolean {
    return (this.resultOverflow.get(key) ?? false) || this.isStepExpanded(key);
  }

  private readonly resultOverflow = new Map<string, boolean>();

  protected toggleStep(key: string): void {
    this.expandedSteps.set(key, !this.expandedSteps.get(key));
  }

  protected isStepExpanded(key: string): boolean {
    return this.expandedSteps.get(key) ?? false;
  }

  /**
   * Tool inputs arrive as raw JSON strings and most tool results are a `JSON.stringify` of
   * the payload, so both land as one unreadable line. Returns the indented form when the
   * value really is a JSON object or array, and `null` for prose, numbers and truncated
   * streaming fragments — the caller falls back to its normal rendering on `null`.
   *
   * Memoised: the template calls this on every change-detection pass, and re-parsing a
   * multi-kilobyte result each time is pure waste.
   */
  protected prettyJson(value: unknown): string | null {
    const parsed = this.parseJsonPayload(value);
    return parsed === undefined ? null : JSON.stringify(parsed, null, 2);
  }

  /**
   * The same payload as a navigable tree.
   *
   * Indented text is an improvement over one long line, but a 21-row export still arrives
   * as a few hundred lines of punctuation that the reader has to parse by eye to answer
   * "what did this tool actually get back". The tree answers that at a glance: containers
   * state their size and stay collapsed below the second level, so the shape is visible
   * before any of the content is.
   */
  protected jsonTree(value: unknown): BesharpJsonNode | null {
    const parsed = this.parseJsonPayload(value);
    if (parsed === undefined) return null;

    const cacheKey = typeof value === 'string' ? value : null;
    if (cacheKey !== null) {
      const cached = this.jsonTreeCache.get(cacheKey);
      if (cached !== undefined) return cached;
    }

    const node = this.buildJsonNode(null, parsed, 0, '');
    if (cacheKey !== null) {
      if (this.jsonTreeCache.size > 200) this.jsonTreeCache.clear();
      this.jsonTreeCache.set(cacheKey, node);
    }
    return node;
  }

  /**
   * Containers above the second level start open, deeper ones start closed — enough to
   * show the shape of a payload without unfolding every row of an export.
   */
  protected isJsonExpanded(key: string, node: BesharpJsonNode): boolean {
    return this.expandedSteps.get(key) ?? node.depth < 2;
  }

  protected toggleJson(key: string, node: BesharpJsonNode): void {
    this.expandedSteps.set(key, !this.isJsonExpanded(key, node));
  }

  /** Long strings are clamped in the view; the full text goes on the row's `title`. */
  protected jsonTitle(node: BesharpJsonNode): string | null {
    return node.kind === 'string' && (node.value?.length ?? 0) > 120 ? (node.value ?? null) : null;
  }

  private readonly prettyJsonCache = new Map<string, unknown>();
  private readonly jsonTreeCache = new Map<string, BesharpJsonNode>();

  /**
   * Parsed object/array, or `undefined` for prose, scalars and half-streamed fragments —
   * every caller falls back to its plain rendering on `undefined`.
   *
   * Memoised: the template calls into this on every change-detection pass, and re-parsing
   * a multi-kilobyte result each time is pure waste.
   */
  private parseJsonPayload(value: unknown): unknown | undefined {
    if (typeof value !== 'string') {
      return value !== null && typeof value === 'object' ? value : undefined;
    }

    const cached = this.prettyJsonCache.get(value);
    if (cached !== undefined) return cached === NO_JSON ? undefined : cached;

    const trimmed = value.trim();
    let parsed: unknown = NO_JSON;
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const candidate: unknown = JSON.parse(trimmed);
        if (candidate !== null && typeof candidate === 'object') parsed = candidate;
      } catch {
        // Half-streamed input, or a string that only looks like JSON. Show it verbatim.
      }
    }

    // Streaming rewrites the input on every chunk, so the cache would otherwise grow one
    // entry per keystroke-sized delta. Drop it wholesale rather than track ages.
    if (this.prettyJsonCache.size > 200) this.prettyJsonCache.clear();
    this.prettyJsonCache.set(value, parsed);
    return parsed === NO_JSON ? undefined : parsed;
  }

  private buildJsonNode(key: string | null, raw: unknown, depth: number, path: string): BesharpJsonNode {
    if (raw === null) return { key, kind: 'null', value: 'null', depth, path };

    if (Array.isArray(raw)) {
      return {
        key,
        kind: 'array',
        size: raw.length,
        depth,
        path,
        children: raw.map((item, i) => this.buildJsonNode(String(i), item, depth + 1, `${path}.${i}`)),
      };
    }

    if (typeof raw === 'object') {
      const entries = Object.entries(raw as Record<string, unknown>);
      return {
        key,
        kind: 'object',
        size: entries.length,
        depth,
        path,
        // Indexed, not keyed: an object with the literal keys `a.b` and `a` (holding `b`)
        // would otherwise produce the same path twice and share one expand state.
        children: entries.map(([k, v], i) => this.buildJsonNode(k, v, depth + 1, `${path}.${i}`)),
      };
    }

    const kind: BesharpJsonNode['kind'] =
      typeof raw === 'number' ? 'number' : typeof raw === 'boolean' ? 'boolean' : 'string';
    return { key, kind, value: String(raw), depth, path };
  }

  /**
   * Groups a flat list of tool steps into displayable units:
   *  - 'standalone': agent_thinking / thinking — rendered as a plain row.
   *  - 'call_group': a tool_call followed by all immediately following
   *    tool_result steps — rendered as a single collapsible accordion.
   */
  /** Tools the orchestrator ran itself read differently from work it delegated, so the two
   * get different colours and icons rather than only different indentation. */
  protected readonly SUB_AGENT_TOOLS = new Set(['rag_sub_agent', 'chart_sub_agent', 'retriever_sub_agent']);

  /**
   * Children worth showing. A sub-agent's streamed thinking is a live preview of the very
   * text that comes back as its result, so once the result lands the preview is duplicate
   * content — and it is the bulkiest kind. Keep it only while the call is still running.
   */
  protected visibleChildren(group: BesharpChatStepGroup): BesharpChatStepGroup[] {
    if (group.kind !== 'call_group') return [];
    const settled = group.results.length > 0;
    return group.children.filter(
      (child) => !(settled && child.kind === 'standalone' && child.step.type === 'sub_agent_thinking')
    );
  }

  /** Every step in the turn, not just the top-level ones — nesting must not change the count. */
  protected countSteps(steps: BesharpChatToolStep[]): number {
    return steps.filter((step) => step.type !== 'sub_agent_thinking').length;
  }

  protected isSubAgentCall(step: BesharpChatToolStep): boolean {
    const name = step.metadata?.['tool_name'] as string | undefined;
    return !!name && this.SUB_AGENT_TOOLS.has(name);
  }

  /** `rag_sub_agent` -> `rag`, so the chip reads as a role and not as a function name. */
  protected subAgentLabel(name: unknown): string {
    return typeof name === 'string' ? name.replace(/_sub_agent$/, '').replace(/_/g, ' ') : 'sub-agent';
  }

  protected getToolGroups(steps: BesharpChatToolStep[]): BesharpChatStepGroup[] {
    type Group = BesharpChatStepGroup;
    const groups: Group[] = [];

    // Pre-collect tool_result steps by toolCallId for ID-based matching
    // (used when multiple tool calls run in parallel and results arrive out of order).
    const resultsByCallId = new Map<string, BesharpChatToolStep[]>();
    for (const step of steps) {
      if (step.type === 'tool_result') {
        const id = step.metadata?.['toolCallId'] as string | undefined;
        if (id) {
          if (!resultsByCallId.has(id)) resultsByCallId.set(id, []);
          resultsByCallId.get(id)!.push(step);
        }
      }
    }

    const claimedResults = new Set<BesharpChatToolStep>();
    let i = 0;
    while (i < steps.length) {
      const step = steps[i];
      if (step.type === 'tool_call') {
        const id = step.metadata?.['toolCallId'] as string | undefined;
        if (id && resultsByCallId.has(id)) {
          // ID-based matching: bind all results that carry this toolCallId.
          const results = resultsByCallId.get(id)!;
          for (const r of results) claimedResults.add(r);
          groups.push({ kind: 'call_group', call: step, results, callIndex: i, children: [] });
          i++;
        } else {
          // Sequential fallback for legacy steps that have no toolCallId.
          const results: BesharpChatToolStep[] = [];
          let j = i + 1;
          while (j < steps.length && steps[j].type === 'tool_result' && !claimedResults.has(steps[j])) {
            claimedResults.add(steps[j]);
            results.push(steps[j]);
            j++;
          }
          groups.push({ kind: 'call_group', call: step, results, callIndex: i, children: [] });
          i = j;
        }
      } else if (step.type === 'tool_result' && claimedResults.has(step)) {
        // Already claimed by an ID-based group — skip it (do not emit standalone).
        i++;
      } else {
        groups.push({ kind: 'standalone', step, index: i });
        i++;
      }
    }

    // Second pass: fold every group that names a parent into that sub-agent's group. A
    // sub-agent always opens before the tools it calls, so the most recent group bearing its
    // name is the right one even when the same sub-agent is invoked twice in a turn. Steps
    // rehydrated from history carry no parent, so they stay flat exactly as before.
    const topLevel: BesharpChatStepGroup[] = [];
    const openByName = new Map<string, Extract<BesharpChatStepGroup, { kind: 'call_group' }>>();
    for (const group of groups) {
      const parentName =
        group.kind === 'call_group'
          ? (group.call.metadata?.['parent'] as string | undefined)
          : group.step.type === 'sub_agent_thinking'
            ? (group.step.metadata?.['sub_agent'] as string | undefined)
            : undefined;
      const parent = parentName ? openByName.get(parentName) : undefined;
      if (parent) {
        parent.children.push(group);
      } else {
        topLevel.push(group);
      }
      if (group.kind === 'call_group') {
        const name = group.call.metadata?.['tool_name'] as string | undefined;
        if (name) openByName.set(name, group);
      }
    }
    return topLevel;
  }

  // ─── Feedback (thumbs up/down) ───────────────────────────────────────────────

  /** Vote already sent for a message, keyed by message id — undefined means "no vote yet". */
  private feedbackGiven = new Map<string, boolean>();
  private feedbackPending = new Set<string>();

  /**
   * Only messages from the live SSE stream carry a `trace_id` — history-reloaded ones
   * never do — so this also gates the buttons off while the answer is still streaming,
   * since the event lands only after the last text chunk.
   */
  protected canGiveFeedback(message: BesharpChatMessage): boolean {
    return message.role === 'assistant' && !!message.trace_id && !!this.service.submitFeedback;
  }

  protected feedbackValue(message: BesharpChatMessage): boolean | undefined {
    return this.feedbackGiven.get(message.id);
  }

  protected isFeedbackPending(message: BesharpChatMessage): boolean {
    return this.feedbackPending.has(message.id);
  }

  protected async sendFeedback(message: BesharpChatMessage, value: boolean): Promise<void> {
    if (!message.trace_id || !this.service.submitFeedback) return;
    if (this.feedbackPending.has(message.id) || this.feedbackGiven.get(message.id) === value) return;

    this.feedbackPending.add(message.id);
    try {
      await this.service.submitFeedback(message.trace_id, value);
      this.feedbackGiven.set(message.id, value);
    } catch {
      this.errorMessage = 'Failed to submit feedback.';
    } finally {
      this.feedbackPending.delete(message.id);
      this.cdr.detectChanges();
    }
  }

  protected dismissError(): void {
    this.errorMessage = null;
  }

  protected get placeholder(): string {
    return this.config.placeholder ?? 'Ask something…';
  }

  protected get botName(): string {
    return this.config.botName ?? 'Assistant';
  }

  protected get userLabel(): string {
    return this.config.userLabel ?? 'You';
  }

  protected get showTimestamps(): boolean {
    return this.config.showTimestamps ?? true;
  }

  protected get emptyState(): BesharpChatEmptyState {
    return {
      icon: this.config.emptyState?.icon ?? 'pi pi-globe',
      title: this.config.emptyState?.title ?? 'ITU Datahub Agent',
      description: this.config.emptyState?.description ?? 'Start a conversation to get information about ITU',
      suggestions: this.config.emptyState?.suggestions ?? [
        "What's the latest value for international bandwidth for Africa?",
        'What does the ITU recommend as the minimum age for ICT household surveys?',
      ],
    };
  }

  /** Skeleton row counts when loading history. */
  protected readonly skeletonRows = [0, 1, 2, 3, 4];

  // ─── Scroll ──────────────────────────────────────────────────────────────────

  private queueStreamChunk(message: BesharpChatMessage, chunk: string): void {
    this.pendingWordQueue.push(...this.tokenizeChunkForStreaming(chunk));
    this.startWordPlayback(message);
  }

  private flushPendingStreamChunk(message: BesharpChatMessage): void {
    this.stopWordPlayback();
    if (this.pendingWordQueue.length === 0) {
      return;
    }

    message.content += this.pendingWordQueue.join('');
    this.pendingWordQueue = [];
    this.cdr.detectChanges();
  }

  private startWordPlayback(message: BesharpChatMessage): void {
    if (this.streamWordIntervalId !== undefined) {
      return;
    }

    this.streamWordIntervalId = window.setInterval(() => {
      if (this.destroyed) {
        this.stopWordPlayback();
        return;
      }

      if (this.pendingWordQueue.length === 0) {
        this.stopWordPlayback();
        return;
      }

      const tokensPerTick = this.pendingWordQueue.length > 90 ? 4 : this.pendingWordQueue.length > 36 ? 2 : 1;
      let nextText = '';

      for (let i = 0; i < tokensPerTick && this.pendingWordQueue.length > 0; i++) {
        nextText += this.pendingWordQueue.shift() ?? '';
      }

      if (!nextText) {
        return;
      }

      message.content += nextText;
      this.cdr.detectChanges();
      this.scrollToBottom();
    }, this.streamWordIntervalMs);
  }

  private stopWordPlayback(): void {
    if (this.streamWordIntervalId === undefined) {
      return;
    }

    window.clearInterval(this.streamWordIntervalId);
    this.streamWordIntervalId = undefined;
  }

  private tokenizeChunkForStreaming(chunk: string): string[] {
    if (!chunk) {
      return [];
    }

    // Keep trailing spaces/newlines with each word to preserve markdown formatting.
    return chunk.match(/\S+\s*|\s+/g) ?? [chunk];
  }

  private scrollToBottom(): void {
    if (this.scrollFrame !== null) {
      return;
    }

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      if (this.messagesContainer) {
        const el = this.messagesContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    });
  }

  private resetTextareaHeight(): void {
    if (this.textInput) {
      this.textInput.nativeElement.style.height = 'auto';
    }
  }
  downloadTable(table: HTMLTableElement): void {
    const headers: string[] = [];
    const headerNodes = table.querySelectorAll('thead th');
    headerNodes.forEach((th: Element) => {
      headers.push((th as HTMLElement).innerText.trim());
    });

    const rows: string[][] = [];
    const rowNodes = table.querySelectorAll('tbody tr');
    rowNodes.forEach((tr: Element) => {
      const rowData: string[] = [];
      const cellNodes = tr.querySelectorAll('td');
      cellNodes.forEach((td: Element) => {
        rowData.push((td as HTMLElement).innerText.trim());
      });
      rows.push(rowData);
    });

    const worksheetData = [headers, ...rows];

    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(worksheetData);

    const workbook: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Table data');

    XLSX.writeFile(workbook, `export_table-DataHub.xlsx`);
  }
}
